import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
  ClientErrorClientApp,
  ClientErrorEvent,
  ClientErrorReport,
} from '../models/errors/client-error.model';

@Injectable({
  providedIn: 'root',
})
export class ClientErrorApiService {
  constructor(private http: HttpClient) {}

  report(payload: ClientErrorReport): Observable<void> {
    return this.http.post<void>('errors/report', payload);
  }

  listErrors(from: string, to: string, size: number = 50): Observable<ClientErrorEvent[]> {
    const params = new URLSearchParams({
      from,
      to,
      size: String(size),
    });
    return this.http.get<ClientErrorEvent[]>(`admin/stats/errors?${params.toString()}`);
  }

  deleteError(id: string): Observable<void> {
    return this.http.delete<void>(`admin/stats/errors/${id}`);
  }

  /**
   * Fire-and-forget report used from HTTP interceptors.
   * Never reports the report endpoint itself (avoids loops) or 401s.
   */
  reportHttpError(
    error: HttpErrorResponse,
    clientApp: ClientErrorClientApp,
    route: string | null,
    requestMethod?: string | null,
  ): void {
    if (!error || error.status < 400 || error.status === 401 || error.status === 403) {
      return;
    }
    const path = this.extractPath(error.url);
    if (path && path.includes('/errors/report')) {
      return;
    }
    if (path && path.includes('/presence/heartbeat')) {
      return;
    }

    const {message, body} = this.extractResponse(error);
    this.report({
      clientApp,
      httpMethod: requestMethod ?? null,
      requestPath: path,
      route,
      statusCode: error.status,
      statusText: error.statusText || null,
      responseMessage: message,
      responseBody: body,
    }).subscribe({
      error: () => {
        // Never escalate report failures.
      },
    });
  }

  private extractPath(url: string | null): string | null {
    if (!url) {
      return null;
    }
    try {
      const parsed = new URL(url, 'http://localhost');
      return parsed.pathname + (parsed.search ? '' : '');
    } catch {
      const query = url.indexOf('?');
      return query >= 0 ? url.substring(0, query) : url;
    }
  }

  private extractResponse(error: HttpErrorResponse): {message: string | null; body: string | null} {
    const raw = error.error;
    if (raw == null) {
      return {message: this.friendlyHttpMessage(error), body: null};
    }
    if (typeof raw === 'string') {
      return {message: raw.slice(0, 1024), body: raw.slice(0, 4000)};
    }
    if (typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      const messageCandidate =
        (typeof obj['title'] === 'string' && obj['title']) ||
        (typeof obj['message'] === 'string' && obj['message']) ||
        (typeof obj['detail'] === 'string' && obj['detail']) ||
        null;
      let body: string | null = null;
      try {
        body = JSON.stringify(raw).slice(0, 4000);
      } catch {
        body = null;
      }
      return {message: messageCandidate ?? this.friendlyHttpMessage(error), body};
    }
    return {message: this.friendlyHttpMessage(error), body: String(raw).slice(0, 4000)};
  }

  /** Prefer status text over Angular's generic "Http failure response for …" message. */
  private friendlyHttpMessage(error: HttpErrorResponse): string | null {
    const statusText = (error.statusText || '').trim();
    if (error.status > 0 && statusText && statusText.toUpperCase() !== 'OK') {
      return `${error.status} ${statusText}`;
    }
    if (error.status > 0) {
      return String(error.status);
    }
    const message = (error.message || '').trim();
    if (message && !message.startsWith('Http failure response')) {
      return message;
    }
    return message || null;
  }
}
